import { partitionBySignalKeys, toSignalTypeKey } from "app/pages/sales-intelligence/domain/signals/utils/signal-key";
import { toSingleVisitsFromCountry } from "app/pages/sales-intelligence/modules/leads-search-filters/store/utils/selectCombiners";
import type { LeadsSearchAllFiltersTypes } from "app/pages/sales-intelligence/modules/leads-search-filters/types/leadsSearchFilters.dto";
import {
    leadsFilterDtoToPanelValue,
    leadsFilterPanelValueToDto,
} from "app/pages/sales-intelligence/modules/leads-search-filters/utils/leadsFilterDtoAdapters";
import { resolveLeadsFiltersDto } from "app/pages/sales-intelligence/modules/leads-search-filters/utils/resolveLeadsFilterPanelValue";
import type { SignalByIdMap } from "app/pages/sales-intelligence/sub-modules/signals/types/extended-models";
import type { IScoringRuleDto } from "app/pages/sales-intelligence/sub-modules/signals/types/scoring-config";

/**
 * Wizard ↔ persisted-rule adapters. The filter panel works in the flat
 * `Partial<LeadsSearchAllFiltersTypes>` shape (signal-type keys + leads filter keys + namespaced
 * CRM keys like `crm:salesforce:Industry`). Per-rule storage splits these:
 *   - `signalIds` — flattened ids across every `signalsXxx` bucket.
 *   - `filters`   — composite `{ leadsFilters, salesforceCustomFilters }` matching the wire format
 *                   used by saved searches; CRM keys fold into `salesforceCustomFilters`.
 */

type ToRuleArgs = {
    panelValue: Partial<LeadsSearchAllFiltersTypes>;
    businessContext: IScoringRuleDto["businessContext"];
    existingId?: string;
};

const generateRuleId = (): string => crypto.randomUUID();

const withSingleVisitsFromCountry = (
    panelValue: Partial<LeadsSearchAllFiltersTypes>,
): Partial<LeadsSearchAllFiltersTypes> => {
    const countries = panelValue.countries;

    if (!Array.isArray(countries)) {
        return panelValue;
    }

    return { ...panelValue, countries: toSingleVisitsFromCountry(countries) };
};

export const panelValueToScoringRule = ({ panelValue, businessContext, existingId }: ToRuleArgs): IScoringRuleDto => {
    const { signals, rest } = partitionBySignalKeys(withSingleVisitsFromCountry(panelValue));

    const signalIds = Object.values(signals)
        .filter((list): list is NonNullable<typeof list> => Array.isArray(list))
        .flatMap((list) => list.map((signal) => signal.id));

    const filters = resolveLeadsFiltersDto(leadsFilterPanelValueToDto(rest));

    return {
        id: existingId ?? generateRuleId(),
        signalIds,
        businessContext,
        ...(Object.keys(filters).length > 0 ? { filters } : {}),
    };
};

export const scoringRuleToPanelValue = (
    rule: IScoringRuleDto,
    signalById: SignalByIdMap,
): Partial<LeadsSearchAllFiltersTypes> => {
    const panelValue: Partial<LeadsSearchAllFiltersTypes> = withSingleVisitsFromCountry(
        leadsFilterDtoToPanelValue(rule.filters ?? {}),
    );

    for (const signalId of rule.signalIds) {
        const signal = signalById.get(signalId);
        if (!signal) {
            continue;
        }
        const key = toSignalTypeKey(signal.signalType);
        const existing = (panelValue[key] as readonly (typeof signal)[] | undefined) ?? [];
        (panelValue[key] as readonly (typeof signal)[]) = [...existing, signal];
    }

    return panelValue;
};
